import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { BtcDkRecord, BtcTvRecord } from "../../services/btc/btcApi";



type BtcConfig = {
  dsQuy?: unknown;
  ngay?: unknown;
  [key: string]: unknown;
};

type BtcState = {
  btcDsDk: BtcDkRecord[];
  email: string;
  start: string;
  end: string;
  btcDsTv: BtcTvRecord[] | undefined;
  btcConfig: BtcConfig;
  status: "idle" | "loading";
};

const initialState: BtcState = {
  btcDsDk: [],
  email: "",
  start: "",
  end: "",
  btcDsTv: undefined,
  btcConfig: {},
  status: "idle",
};

// localLoadData(); // reduce fetch on debug

export const slice = createSlice({
  name: "btc",
  initialState,
  reducers: {
    addOrEditBtcDk: (
      state,
      action: PayloadAction<{ id: string; btcDk: BtcDkRecord }>,
    ) => {
      const { id, btcDk } = action.payload;
      const btc = state.btcDsDk.find((u) => u.id === id);
      if (btc) {
        patch(btc, btcDk);
      } else {
        state.btcDsDk.push({ ...btcDk, id });
      }
    },
    editBtcDk: (
      state,
      action: PayloadAction<{ id: string; changes: Partial<BtcDkRecord> }>,
    ) => {
      const { id, changes } = action.payload;
      const btc = state.btcDsDk.find((u) => u.id === id);
      if (btc) {
        patch(btc, changes);
      }
    },
    addBtcDk: (state, action: PayloadAction<{ btcDk: BtcDkRecord }>) => {
      state.btcDsDk.push(action.payload.btcDk);
    },
    addOrUpdateBtcDk: (
      state,
      action: PayloadAction<BtcDkRecord | BtcDkRecord[]>,
    ) => {
      const records = Array.isArray(action.payload)
        ? action.payload
        : [action.payload];

      const map = new Map(state.btcDsDk.map((r) => [r.id, r]));

      for (const record of records) {
        map.set(record.id, record);
      }

      state.btcDsDk = Array.from(map.values());
    },
    deleteBtcDk: (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload;
      const index = state.btcDsDk.findIndex((u) => u.id === id);
      if (index !== -1) {
        state.btcDsDk.splice(index, 1);
      }
    },
    setBtcDsDk: (
      state,
      action: PayloadAction<{
        btcDsDk: BtcDkRecord[];
        email: string;
        start: string;
        end: string;
      }>,
    ) => {
      const { btcDsDk, email, start, end } = action.payload;
      state.btcDsDk = btcDsDk;
      state.email = email;
      state.start = start;
      state.end = end;
    },
    setBtcRng: (
      state,
      action: PayloadAction<{ start: string; end: string }>,
    ) => {
      const { start, end } = action.payload;
      state.start = start;
      state.end = end;
    },
    editBtcTv: (
      state,
      action: PayloadAction<{ id: string; changes: Partial<BtcTvRecord> }>,
    ) => {
      const { id, changes } = action.payload;
      if (!state.btcDsTv) return;
      
      const tv = state.btcDsTv.find((u) => u.id === id);
      if (tv) {
        patch(tv, changes);
      }
    },
    addBtcTv: (state, action: PayloadAction<{ btcTv: BtcTvRecord }>) => {
      if (!state.btcDsTv) {
        state.btcDsTv = [action.payload.btcTv];
        return;
      }

      state.btcDsTv.push(action.payload.btcTv);
    },
    removeBtcTv: (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload;
      if (!state.btcDsTv) return;

      const index = state.btcDsTv.findIndex((u) => u.id === id);
      if (index !== -1) {
        state.btcDsTv.splice(index, 1);
      }
    },
    setBtcDsTv: (state, action: PayloadAction<{ btcDsTv: BtcTvRecord[] }>) => {
      state.btcDsTv = action.payload.btcDsTv;
    },
    addOrUpdateBtcTv: (
      state,
      action: PayloadAction<BtcTvRecord | BtcTvRecord[]>,
    ) => {
      const records = Array.isArray(action.payload)
        ? action.payload
        : [action.payload];

      if (!state.btcDsTv) {
        state.btcDsTv = records;
        return;
      }

      const map = new Map(state.btcDsTv.map((r) => [r.id, r]));

      for (const record of records) {
        map.set(record.id, record);
      }

      state.btcDsTv = Array.from(map.values());
    },
    setBtcConfig: (state, action: PayloadAction<BtcConfig>) => {
      state.btcConfig = action.payload;
    },
    updateBtcConfig: (state, action: PayloadAction<Partial<BtcConfig>>) => {
      patch(state.btcConfig, action.payload);
    },
  },
  // extraReducers: (builder) => {
  //   builder
  //     .addCase(fetchDataAsync.fulfilled, (state, action) => {
  //       state.status = "idle";
  //       console.log("fetchWorkingTimeAsync", action);
  //       if (action.payload) {
  //         state.sheetData = action.payload.sheetData;
  //         state.sheetHdr = action.payload.sheetHdr;
  //       }
  //     })
  //     .addCase(fetchDataAsync.pending, (state) => {
  //       state.status = "loading";
  //     });
  // },
});

export const {
  addOrEditBtcDk,
  setBtcRng,
  editBtcDk,
  addBtcDk,
  addOrUpdateBtcDk,
  deleteBtcDk,
  setBtcDsDk,
  editBtcTv,
  addBtcTv,
  removeBtcTv,
  setBtcDsTv,
  addOrUpdateBtcTv,
  setBtcConfig,
  updateBtcConfig,
} = slice.actions;

type RootState = {
  btc: BtcState;
};

export const selectAllBtcDk = (state: RootState) => state.btc.btcDsDk;
export const selectBtcEmail = (state: RootState) => state.btc.email;
export const selectBtcStart = (state: RootState) => state.btc.start;
export const selectBtcEnd = (state: RootState) => state.btc.end;
export const selectBtc = (state: RootState) => state.btc;
export const selectBtcDkById = (state: RootState, id: string) => state.btc.btcDsDk.find((u) => u.id === id);
export const selectBtcDsTv = (state: RootState) => state.btc.btcDsTv;
export const selectBtcConfig = (state: RootState) => state.btc.btcConfig;

export default slice.reducer;
function patch<T extends object>(entity: T | undefined, changes: Partial<T>) {
  if (!entity) return;
  Object.keys(changes).forEach((key) => {
    const value = (changes as Record<string, unknown>)[key];
    (entity as Record<string, unknown>)[key] = value;
  });
}

